import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Box, Grid } from "@mui/material";
import ImportFile from "../../../../components/import-file/import-file.component";
import ProgressIndicator from "../../../../components/progress/progress-indicator.component";
import { useImportProgress } from "../../../../hooks/useImportProgress";
import { translate } from "../../../../translate/translate";
import { ImportStatus } from "../../../../types/import.types";

const ImportBom = () => {
  const { isImporting, currentImport, historyVersion, requestImportStatus, setImportingState, clearImport } = useImportProgress('bom');

  /**
   * Chamado assim que o envio começa.
   * Ativa o acompanhamento imediatamente sem depender da resposta final do POST.
   */
  const handleUploadStart = () => {
    clearImport();
    setImportingState(true);
    requestImportStatus();
    setTimeout(() => {
      requestImportStatus();
    }, 500);
    setTimeout(() => {
      requestImportStatus();
    }, 2000);
  };

  /**
   * Mantém a atualização de status também quando o backend só registra o job
   * depois de concluir o upload.
   */
  const handleUploadSuccess = () => {
    requestImportStatus();
  };

  const handleUploadError = () => {
    setImportingState(false);
    clearImport();
  };

  // Dispara toast de erro quando a importação falha via WebSocket
  useEffect(() => {
    if (
      currentImport?.status === ImportStatus.ERROR ||
      currentImport?.status === ImportStatus.FAILED
    ) {
      toast.error(translate.t("failed_to_upload_file"), {
        autoClose: false,
      });
    }
  }, [currentImport?.status]);

  useEffect(() => {
    if (!isImporting) return;

    requestImportStatus();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestImportStatus();
      }
    };

    window.addEventListener("focus", requestImportStatus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", requestImportStatus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isImporting, requestImportStatus]);

  const progressValue = useMemo(() => {
    if (!currentImport) return 0;
    const raw = (currentImport.progress ?? 0) * 100;
    return Math.round(raw);
  }, [currentImport]);

  const titleText = useMemo(() => {
    if (!currentImport) return translate.t("import_in_progress");

    switch (currentImport.status) {
      case ImportStatus.PENDING:
        return translate.t("waiting_in_queue");
      case ImportStatus.RUNNING:
        return `${translate.t("import_in_progress")} (${progressValue}%)`;
      case ImportStatus.ERROR:
      case ImportStatus.FAILED:
        return translate.t("import_failed");
      default:
        return `${translate.t("import_in_progress")} (${progressValue}%)`;
    }
  }, [currentImport, progressValue]);

  /**
   * Formata segundos em formato legível (X horas e Y minutos ou X minutos)
   */
  const formatRemainingTime = (seconds: number): string => {
    if (seconds <= 0) return "";

    const totalMinutes = Math.ceil(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // Usa toLowerCase() pois as strings de tradução começam com maiúscula
    const hourStr = (hours === 1 ? translate.t("hour") : translate.t("hours")).toLowerCase();
    const minuteStr = (minutes === 1 ? translate.t("minute") : translate.t("minutes")).toLowerCase();
    const andStr = translate.t("and");

    if (hours > 0 && minutes > 0) {
      return `${hours} ${hourStr} ${andStr} ${minutes} ${minuteStr}`;
    } else if (hours > 0) {
      return `${hours} ${hourStr}`;
    } else {
      return `${minutes} ${minuteStr}`;
    }
  };

  const subtitleText = useMemo(() => {
    if (!currentImport) return "";

    // Se for erro, mostra a mensagem de erro
    if (currentImport.status === ImportStatus.ERROR || currentImport.status === ImportStatus.FAILED) {
      return currentImport.error_message ?? currentImport.error ?? "";
    }

    // Se tem tempo restante, mostra o tempo estimado
    if (currentImport.remaining_seconds && currentImport.remaining_seconds > 0) {
      const formattedTime = formatRemainingTime(currentImport.remaining_seconds);
      return `${translate.t("time_remaining")} ${formattedTime}`;
    }

    // Fallback: mostra linhas processadas
    if (currentImport.total_rows && currentImport.processed_rows !== undefined) {
      return `${currentImport.processed_rows} / ${currentImport.total_rows} ${translate.t("rows")}`;
    }

    if (currentImport.message) {
      return currentImport.message;
    }

    return translate.t("processing_file");
  }, [currentImport]);

  // Sempre renderiza ImportFile, mas oculta upload se houver importação em andamento
  const isError = currentImport?.status === ImportStatus.ERROR || currentImport?.status === ImportStatus.FAILED;
  const shouldShowProgress = isImporting || isError;
  const isPendingFeedback = isImporting && !currentImport;

  return (
    <Grid item xs={12} sx={{ paddingBottom: "16px" }}>
      <ImportFile
        type={"bom"}
        hideUpload={isImporting}
        reloadTrigger={historyVersion}
        onUploadStart={handleUploadStart}
        onUploadSuccess={handleUploadSuccess}
        onUploadError={handleUploadError}
      >
        {shouldShowProgress && (
          <Grid item gap={2} sx={{ padding: 3 }}>
            <Box
              border="1px solid #CFD8DC"
              borderRadius={1}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                paddingBottom: 3,
              }}
            >
              <ProgressIndicator
                value={isPendingFeedback ? 0 : progressValue}
                title={isPendingFeedback ? translate.t("waiting_in_queue") : titleText}
                subtitle={isPendingFeedback ? translate.t("processing_file") : subtitleText}
                additionalText={isError ? undefined : (translate.t("process_information") || "Não feche esta página durante o processamento")}
                variant={isPendingFeedback || currentImport?.status === ImportStatus.PENDING ? "indeterminate" : "determinate"}
                showCircular={!isError}
              />
            </Box>
          </Grid>
        )}
      </ImportFile>
    </Grid>
  );
};

export default ImportBom;
